import React, { useEffect, useState, useCallback } from 'react';
import { Box, Card, Tab, Tabs } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { AnnouncementLevel, IAnnouncement, IAnnouncementListing } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddAnnouncement from './add-announcement';
import { EmailOutlined } from '@mui/icons-material';

type LanguageTab = 'en' | 'ar' | 'fr';
type PopupFlag = 'Popup' | 'Normal';

const AnnouncementListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [allData, setAllData] = useState<IAnnouncement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddAnnouncement, setOpenAddAnnouncement] = useState<boolean>(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<IAnnouncement | null>(null);
  const [activeTab, setActiveTab] = useState<LanguageTab>('en');

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this announcement?',
      onSubmit: async () => {
        const result = await DataService.post('api/Announcement/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Announcement activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate announcement', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this announcement?',
      onSubmit: async () => {
        const result = await DataService.post('api/Announcement/deactivate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Announcement deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate announcement', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const sendAsEmail = useCallback((id: string) => {
  const confirmation: IConfirmation = {
    open: true,
    title: 'Send as Email',
    message: 'Do you want to send this announcement as an email notification?',
    onSubmit: async () => {
      try {
        const result = await DataService.post(`api/Announcement/sendAsEmail/${id}`, {});
        if (result.ok) {
          dispatch(showAlert({
            message: 'Announcement email dispatched successfully!',
            type: 'success'
          }));
        } else {
          dispatch(showAlert({
            message: 'Failed to send announcement as email',
            type: 'error'
          }));
        }
      } catch (error) {
        dispatch(showAlert({
          message: 'Error sending announcement as email',
          type: 'error'
        }));
      }
    }
  };

  dispatch(OpenConfirmation(confirmation));
}, [dispatch]);


  const deleteAnnouncement = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this announcement? This action cannot be undone.',
      onSubmit: async () => {
        const result = await DataService.delete('api/Announcement/delete/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Announcement deleted successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to delete announcement', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editAnnouncement = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/Announcement/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const announcement: IAnnouncement = res.data;
        setSelectedAnnouncement(announcement);
        setOpenAddAnnouncement(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch announcement details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading announcement details', type: 'error' }));
    }
  }, [dispatch]);

  const levelChipColors = {
    Info:      { backgroundColor: '#e8f2ff', color: '#0b5ddd' },
    Advisory:  { backgroundColor: '#fff7e6', color: '#a15c00' },
    Important: { backgroundColor: '#ffeede', color: '#b93815' },
    Critical:  { backgroundColor: '#ffe6e6', color: '#b42318' }
  } as const;

  // NEW: Chip colors for Popup indicator
  const popupChipColors = {
    Popup:  { backgroundColor: '#fff7cc', color: '#7a5d00' },
    Normal: { backgroundColor: '#f3f4f6', color: '#374151' }
  } as const;

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    { id: 'body', label: 'Body', type: EnhancedTableColumnType.TEXT },
    {
      id: 'level',
      label: 'Level',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: levelChipColors
    },
    // NEW: Popup column
    {
      id: 'popup',
      label: 'Popup',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: popupChipColors
    },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: editAnnouncement,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit Announcement'
        },
        {
          onClick: sendAsEmail,
          icon: <EmailOutlined fontSize="small" />,
          label: 'Send as Email',
          hidden: (r) => r.active === false
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate Announcement',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate Announcement',
          hidden: (r) => r.active === false
        },
        {
          onClick: deleteAnnouncement,
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete Announcement',
          color: 'error'
        }
      ]
    }
  ], [activate, deactivate, deleteAnnouncement, editAnnouncement, sendAsEmail]);

  const toLevel = (v: unknown): AnnouncementLevel => {
    if (Object.values(AnnouncementLevel).includes(v as AnnouncementLevel)) {
      return v as AnnouncementLevel;
    }
    if (typeof v === 'string') {
      const hit = (Object.values(AnnouncementLevel) as string[]).find(x => x.toLowerCase() === v.toLowerCase());
      if (hit) return hit as AnnouncementLevel;
    }
    return AnnouncementLevel.Info;
  };

  const getFilteredData = useCallback((language: LanguageTab): Record<string, IAnnouncementListing & { popup: PopupFlag }> => {
    const result: Record<string, IAnnouncementListing & { popup: PopupFlag }> = {};

    allData
      .filter(a => a.language === language)
      .forEach((announcement) => {
        result[announcement.id] = {
          id: announcement.id,
          title: announcement.title,
          body: announcement.body.length > 100 ? announcement.body.substring(0, 100) + '...' : announcement.body,
          language: announcement.language,
          level: toLevel(announcement.level),
          active: announcement.active,
          status: announcement.active ? 'Active' : 'Inactive',
          popup: announcement.isPopup ? 'Popup' as PopupFlag : 'Normal' as PopupFlag
        };
      });

    return result;
  }, [allData]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/Announcement/all');
      if (response.ok) {
        const res = await response.json();
        const list: IAnnouncement[] = res.data;
        setAllData(list);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch announcements', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading announcements', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddAnnouncement(false);
    setSelectedAnnouncement(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: selectedAnnouncement ? 'Announcement updated successfully!' : 'Announcement added successfully!',
      type: 'success'
    }));
  }, [dispatch, selectedAnnouncement]);

  const handleClose = useCallback(() => {
    setOpenAddAnnouncement(false);
    setSelectedAnnouncement(null);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: LanguageTab) => {
    setActiveTab(newValue);
  };

  const actions: ITableMenuAction[] = [];

  if (loading) {
    return (
      <Box>
        <Helmet>
          <title>Announcement List</title>
        </Helmet>
        <MainPageTitle title={'Announcements List'} />
        <Loader />
      </Box>
    );
  }

  const currentData = getFilteredData(activeTab);

  return (
    <div>
      <Helmet>
        <title>Announcement List</title>
      </Helmet>
      <MainPageTitle
        title={'Announcements List'}
        action={{
          title: 'Add Announcement',
          onClick: () => {
            setOpenAddAnnouncement(true);
          }
        }}
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="announcement language tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: '#fff',
            }
          }}
        >
          <Tab label="English" value="en" />
          <Tab label="العربية" value="ar" />
          <Tab label="Français" value="fr" />
        </Tabs>
      </Card>

      <EnhancedTable
        title={`Announcements (${activeTab.toUpperCase()})`}
        columns={headers}
        data={currentData}
        actions={actions}
      />
      {openAddAnnouncement && (
        <AddAnnouncement
          onSuccess={handleAddSuccess}
          onClose={handleClose}
          announcement={selectedAnnouncement}
        />
      )}
    </div>
  );
};

export default AnnouncementListing;
